import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

/**
 * IoT Report Export Controller
 *
 * Intercepts the Export CSV/PDF submit buttons in the shared reports filter
 * bar so the trigger shows a loading state, gets disabled, and the dropdown
 * menu closes on click. The form's other submitter (Apply) has no `format`
 * field and is deliberately left alone to keep Turbo-navigating normally.
 *
 * Mounted either on the filter form itself (inline card) or on a wrapper
 * around the toolbar and the filters drawer (drawer mode), where the Export
 * buttons sit outside the form and name it with the `form` attribute. The
 * `submit` action is always declared on the form.
 *
 * Downloads via `@rails/request.js` + blob (same pattern as
 * Catalog::ExportController) instead of a plain form submission: a native
 * submit gives no JS-visible signal for "the file finished downloading" to
 * clear the loading state from, and Turbo's own fetch-based visit swallows
 * a Content-Disposition: attachment response outright — the two export
 * buttons carry data-turbo="false" as the no-JS fallback for that exact
 * failure mode, so this controller doubles as the reason that attribute is
 * safe to keep: JS intercepts first via `preventDefault`, before Turbo ever
 * sees the submit.
 *
 * A failed export (500/401/…) must NOT look like a success: `get()` doesn't
 * throw on a non-2xx status, so without the explicit `raw.ok` check below,
 * the error response's body would silently download as a file named
 * "export" while the trigger resets to idle exactly like a real success.
 * The catch clause clones the errorToast <template> (rendered by the view)
 * into the shared #toast-container to surface that.
 *
 * What gets exported (CONEK-9496): in drawer mode the form lives in a drawer
 * that only applies on Apply, so it can hold values the page is NOT showing
 * (changed, drawer closed, never applied). There the caller passes the
 * applied query string as `appliedQuery` and the export sends that plus the
 * submitter's `format` — the same filters as the page, its chips and Save this
 * view. Without the value (an inspection's own PDF download, whose form IS the
 * source) the form's fields are sent as before.
 */
export default class extends Controller {
  static targets = ["trigger", "idleLabel", "loadingLabel", "errorToast"]
  static values = { appliedQuery: String }

  // Turbo snapshots the page on the way out. A snapshot taken mid-export would
  // cache the DISABLED trigger and the spinner, so a back-button restore brings
  // back a form that looks permanently busy — and the labels are inline styles
  // the snapshot keeps verbatim. Reset before the snapshot rather than trusting
  // the request to have finished.
  connect() {
    this.resetBusy = () => this.setBusy(false)
    document.addEventListener("turbo:before-cache", this.resetBusy)
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.resetBusy)
  }

  async submit(event) {
    const submitter = event.submitter
    if (!submitter || submitter.name !== "format") return

    event.preventDefault()

    // The form that fired the submit, not this.element: in the reports' drawer
    // mode the controller mounts on a wrapper around the toolbar and the
    // drawer, and the Export submitters reach the form through `form=`.
    const form = event.target

    // The query is built BEFORE setBusy disables the trigger, and that order is
    // load-bearing: a DISABLED submit button contributes no entry at all to
    // `new FormData(form, submitter)`, so building it after the disable drops
    // the submitter's `format=csv|pdf` from the request. The server then sees a
    // plain HTML request, renders the page instead of the export, and the
    // browser saves that page as "export.html" — a silent success-shaped
    // failure with no toast (CONEK-8259).
    const query = this.hasAppliedQueryValue
      ? this.appliedQueryWith(submitter)
      : new FormData(form, submitter)

    this.setBusy(true)

    try {
      const response = await get(form.action, {
        query,
        responseKind: "*/*",
      })
      const raw = response.response
      if (!raw.ok) throw new Error(`Export failed with status ${raw.status}`)

      const blob = await raw.blob()
      const disposition = raw.headers.get("Content-Disposition") || ""
      this.triggerDownload(blob, this.filenameFrom(disposition))
    } catch (error) {
      console.error("Export failed:", error)
      if (this.hasErrorToastTarget) this.showToast(this.errorToastTarget)
    } finally {
      this.setBusy(false)
    }
  }

  // The applied filters plus the submitter's own field (`format=csv|pdf`).
  appliedQueryWith(submitter) {
    const query = new URLSearchParams(this.appliedQueryValue)
    query.set(submitter.name, submitter.value)
    return query
  }

  // Toasts are rendered server-side into a <template> tag by the view, so the
  // copy stays in Rails i18n and no design-system classes leak into JS (same
  // pattern as Catalog::TreeController#showToast). Cloning into
  // #toast-container lets Stimulus connect ui--toast (animation + dismiss).
  showToast(template) {
    const container = document.getElementById("toast-container")
    if (!container) return

    const toast = template.content.firstElementChild.cloneNode(true)
    toast.id = `toast-export-${Math.random().toString(36).slice(2, 10)}`
    container.appendChild(toast)
  }

  triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = filename || "export"
    document.body.appendChild(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  }

  // Parse the filename from a Content-Disposition header, preferring the
  // RFC 5987 `filename*=UTF-8''…` form when present.
  filenameFrom(disposition) {
    const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)
    if (encoded) return decodeURIComponent(encoded[1])
    const plain = disposition.match(/filename="?([^";]+)"?/i)
    return plain ? plain[1] : null
  }

  // Toggle `visibility` (not `display`) via inline style: both labels share
  // one grid cell ([grid-area:1/1] in the view) so the trigger's width/height
  // is always sized to the larger of the two — swapping via `display` would
  // drop the hidden one out of layout and shrink the button to the icon's
  // width while exporting. An inline style also always wins the cascade,
  // unlike toggling the "hidden" class against "inline-flex": both are
  // unconditional display utilities, and which one wins depends on
  // Tailwind's internal generation order rather than class attribute order —
  // unreliable enough that both labels rendered at once in practice.
  setBusy(busy) {
    if (this.hasTriggerTarget) {
      this.triggerTarget.toggleAttribute("disabled", busy)
      // `disabled` alone is not announced by every screen reader once focus is
      // already inside the control, and the design system's own busy controls
      // carry this attribute — so the two states are kept in step rather than
      // only the one the CSS and the submit path read.
      this.triggerTarget.setAttribute("aria-disabled", busy ? "true" : "false")
    }
    if (this.hasIdleLabelTarget)
      this.idleLabelTarget.style.visibility = busy ? "hidden" : "visible"
    if (this.hasLoadingLabelTarget)
      this.loadingLabelTarget.style.visibility = busy ? "visible" : "hidden"
  }
}
